import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Zap } from 'lucide-react';
import { usePWA } from '../hooks/usePWA';

const PWAInstallPrompt = () => {
  const { isInstallable, isInstalled, installApp } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Show prompt after a delay if the app is installable and not already installed
    const timer = setTimeout(() => {
      if (isInstallable && !isInstalled) {
        setShowPrompt(true);
      }
    }, 3000); // Show after 3 seconds

    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled]);

  const handleInstall = async () => {
    console.log('PWA Install: Button clicked, attempting installation...');
    setIsInstalling(true);
    
    const success = await installApp();
    console.log('PWA Install: Installation result:', success);
    
    setIsInstalling(false);
    
    if (success) {
      console.log('PWA Install: Installation successful, hiding prompt');
      setShowPrompt(false);
    } else {
      console.error('PWA Install: Installation failed or was cancelled');
      // You might want to show an error message to the user here
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed, not installable, or dismissed this session
  if (!showPrompt || isInstalled || !isInstallable || sessionStorage.getItem('pwa-prompt-dismissed')) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-2xl p-6 shadow-2xl border border-white/20 backdrop-blur-sm">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/80 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        <div className="flex items-start space-x-4">
          <div className="bg-white/20 rounded-xl p-3 flex-shrink-0">
            <Smartphone className="text-white" size={24} />
          </div>
          
          <div className="min-w-0 flex-1">
            <h3 className="text-white font-semibold text-lg mb-2">
              Install Shot Clock Dating
            </h3>
            <p className="text-rose-100 text-sm mb-4 leading-relaxed">
              Get the full app experience! Install for faster access, offline features, and notifications.
            </p>
            
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex items-center space-x-1 text-rose-100 text-xs">
                <Zap size={14} />
                <span>Faster loading</span>
              </div>
              <div className="w-1 h-1 bg-rose-200 rounded-full"></div>
              <div className="text-rose-100 text-xs">Works offline</div>
              <div className="w-1 h-1 bg-rose-200 rounded-full"></div>
              <div className="text-rose-100 text-xs">Push notifications</div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleInstall}
                disabled={isInstalling}
                className="flex-1 bg-white text-rose-600 font-semibold py-2.5 px-4 rounded-xl hover:bg-rose-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isInstalling ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-rose-600 border-t-transparent"></div>
                    <span>Installing...</span>
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    <span>Install</span>
                  </>
                )}
              </button>
              
              <button
                onClick={handleDismiss}
                className="px-4 py-2.5 text-white/80 hover:text-white transition-colors text-sm font-medium"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;